/**
 * A text's practice status in words (handoff §7, §9.1): «не тренировали», «начато
 * {когда} · {a} из {n}» (or the text screen's longer «начато {когда}: заполнено {a},
 * осталось {b}»), «пройден {k} раза». One place, so the text screen and the three list
 * surfaces say the same thing. Every figure is the server's (`TextPracticeSummary`).
 */
import type { AppTranslations } from '../i18n/app'
import type { TextPracticeSummary } from '../types'

/** The API writes zone-less UTC: read it as UTC. */
export function apiDate(value: string): Date {
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`)
}

/** «сегодня», «вчера», or the day and month in the reader's locale. */
export function whenStarted(value: string, tr: AppTranslations, tag: string, now: Date = new Date()): string {
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diff = Math.round((day(now) - day(apiDate(value))) / 86_400_000)
  if (diff <= 0) return tr.textPlayer.today
  if (diff === 1) return tr.textPlayer.yesterday
  return new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'long' }).format(apiDate(value))
}

/**
 * The status, or null for a text with no gaps (nothing to practise). An attempt in
 * progress wins over completed ones; `long` is the text screen's wording.
 */
export function practiceStatus(
  practice: TextPracticeSummary | null | undefined,
  gapCount: number,
  tr: AppTranslations,
  tag: string,
  style: 'short' | 'long' = 'short',
  now: Date = new Date(),
): string | null {
  if (gapCount === 0) return null
  const running = practice?.in_progress
  if (running) {
    const when = whenStarted(running.started_at, tr, tag, now)
    return style === 'long'
      ? tr.textPlayer.statusStartedLong(when, running.answered, Math.max(0, running.total - running.answered))
      : tr.textPlayer.statusStarted(when, running.answered, running.total)
  }
  if (practice && practice.completed_count > 0) return tr.textPlayer.statusCompleted(practice.completed_count)
  return tr.textList.notPractised
}
